import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Printer } from 'lucide-react';

export const LegalPageLayout = ({ title, lastUpdated, toc, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navLink = (path, label) => (
    <button
      key={path}
      onClick={() => navigate(path)}
      data-testid={`legal-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`text-sm transition-colors ${
        location.pathname === path
          ? 'text-gray-900 font-semibold'
          : 'text-gray-500 hover:text-gray-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-200 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="legal-brand-home"
            className="flex items-center gap-2.5"
          >
            <img src="/logo-icon.png" alt="BookingFlows" className="w-7 h-7 rounded-lg object-cover" />
            <span className="text-[15px] font-bold text-gray-900 tracking-tight">BookingFlows</span>
            <span className="text-[15px] text-gray-300 font-light">/</span>
            <span className="text-[15px] text-gray-500">Legal</span>
          </button>
          <div className="flex items-center gap-5">
            {navLink('/terms', 'Terms of Service')}
            {navLink('/privacy', 'Privacy Policy')}
            <button
              onClick={() => window.print()}
              data-testid="legal-print-btn"
              title="Print this document"
              className="text-gray-400 hover:text-gray-900 transition-colors"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="lg:grid lg:grid-cols-[230px_1fr] lg:gap-16">
          <aside className="hidden lg:block print:hidden">
            <div className="sticky top-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 mb-4">Contents</p>
              <nav className="space-y-1 border-l border-gray-200">
                {toc.map((item, i) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block pl-4 -ml-px border-l border-transparent py-1 text-[13px] leading-5 text-gray-500 hover:text-gray-900 hover:border-gray-900 transition-colors"
                  >
                    <span className="text-gray-400 tabular-nums mr-1.5">{i + 1}.</span>
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <main className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">{title}</h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-500 pb-8 mb-2 border-b border-gray-200">
              <span>Effective date: <span className="text-gray-700 font-medium">{lastUpdated}</span></span>
              <span>Applies to: <span className="text-gray-700 font-medium">Operators &amp; Customers worldwide</span></span>
            </div>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export const LegalSection = ({ id, number, title, children }) => (
  <section id={id} className="scroll-mt-20 pt-8">
    <h2 className="text-lg font-semibold tracking-tight text-gray-900 mb-3">
      <span className="text-gray-400 mr-2 tabular-nums">{number}.</span>
      {title}
    </h2>
    <div className="text-[15px] leading-7 text-gray-600 space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_strong]:text-gray-800 [&_a]:text-blue-600 hover:[&_a]:underline">
      {children}
    </div>
  </section>
);

export const LegalCallout = ({ children }) => (
  <div className="border-l-2 border-gray-900 pl-4 py-1 text-gray-700">
    {children}
  </div>
);
